import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { useEffect, useRef, useState } from 'react';
import type { RouteObject } from 'react-router';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useNavigation,
} from 'react-router';

import { DefaultErrorElement } from 'src/app/DefaultErrorElement';
import { NotFoundRoute } from 'src/app/NotFoundRoute';
import { DefaultRouteLayout } from 'src/common/DefaultRouteLayout';
import type { RoleEnum } from 'src/common/external/bambi-api/bambiApi';
import { AdminLayout } from 'src/features/admin/AdminLayout';
import { adminRoutes } from 'src/features/admin/routes';
import { useUserRoles } from 'src/features/auth/useUserRoles';
import { invoiceRoutes } from 'src/features/billing/routes';
import { ChatLayout } from 'src/features/chat/ChatLayout';
import { chatRoutes } from 'src/features/chat/routes';
import { dispatchRoutes } from 'src/features/dispatch/routes';
import { FleetLayout } from 'src/features/fleet/FleetLayout';
import { fleetRoutes } from 'src/features/fleet/routes';
import { liveMapRoutes } from 'src/features/live-map/routes';
import { notificationsRoutes } from 'src/features/notifications/routes';
import { passengerRoutes } from 'src/features/passenger/routes';
import { payerRoutes } from 'src/features/payer/routes';
import { PricingLayout } from 'src/features/pricing/PricingLayout';
import { pricingRoutes } from 'src/features/pricing/routes';
import { reportsRoutes } from 'src/features/reports/routes';
import { settingsRoutes } from 'src/features/settings/routes';
import { SettingsLayout } from 'src/features/settings/SettingsLayout';
import {
  rideOrderingPortalTeamRoutes,
  teamRoutes,
} from 'src/features/team/routes';
import {
  rideOrderingPortalTripRoutes,
  tripRoutes,
} from 'src/features/trip/routes';

function RootLayout() {
  const navigation = useNavigation();
  const isNavigating = Boolean(navigation.location);
  const hasNavigated = useRef(false);
  const [showLoading, setShowLoading] = useState(false);

  useEffect(() => {
    if (isNavigating) {
      if (!hasNavigated.current) {
        // Show immediately on initial page load — no delay
        setShowLoading(true);
      } else {
        // Delay on subsequent navigations to avoid flash for fast loads
        const timer = setTimeout(() => setShowLoading(true), 150);
        return () => clearTimeout(timer);
      }
    } else {
      hasNavigated.current = true;
      setShowLoading(false);
    }
  }, [isNavigating]);

  return (
    <>
      {showLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60">
          <LoadingIndicator />
        </div>
      )}
      <Outlet />
    </>
  );
}

function withRootLayout(children: RouteObject[]): RouteObject[] {
  return [
    {
      Component: RootLayout,
      errorElement: <DefaultErrorElement />,
      children,
    },
  ];
}

const teamRoute = (children: RouteObject[]): RouteObject => ({
  path: '/team/*',
  lazy: () =>
    import('src/features/team/TeamLayout').then((m) => ({
      Component: m.TeamLayout,
    })),
  children,
  errorElement: <DefaultErrorElement />,
});

const fleetRoute: RouteObject = {
  path: '/fleet/*',
  Component: FleetLayout,
  children: fleetRoutes,
  errorElement: <DefaultErrorElement />,
};

const passengerRoute: RouteObject = {
  path: '/passengers/*',
  lazy: () =>
    import('src/features/passenger/PassengerLayout').then((m) => ({
      Component: m.PassengerLayout,
    })),
  children: passengerRoutes,
  errorElement: <DefaultErrorElement />,
};

const payerRoute: RouteObject = {
  path: '/payers/*',
  lazy: () =>
    import('src/features/payer/PayerLayout').then((m) => ({
      Component: m.PayerLayout,
    })),
  children: payerRoutes,
  errorElement: <DefaultErrorElement />,
};

const settingsRoute: RouteObject = {
  path: '/settings/*',
  Component: SettingsLayout,
  children: settingsRoutes,
  errorElement: <DefaultErrorElement />,
};

const chatRoute: RouteObject = {
  path: '/chat/*',
  Component: ChatLayout,
  children: chatRoutes,
  errorElement: <DefaultErrorElement />,
};

const liveMapRoute: RouteObject = {
  path: '/live-map',
  element: <DefaultRouteLayout />,
  children: liveMapRoutes,
  errorElement: <DefaultErrorElement />,
};

export const routes: RouteObject[] = withRootLayout([
  {
    path: '/',
    element: <Navigate to="/dispatch" />,
    errorElement: <DefaultErrorElement />,
  },
  {
    path: '/dispatch',
    element: <DefaultRouteLayout />,
    children: dispatchRoutes,
    errorElement: <DefaultErrorElement />,
  },
  liveMapRoute,
  teamRoute(teamRoutes),
  fleetRoute,
  passengerRoute,
  {
    path: '/trips/*',
    element: <DefaultRouteLayout />,
    children: tripRoutes,
    errorElement: <DefaultErrorElement />,
  },
  payerRoute,
  {
    path: '/pricing/*',
    Component: PricingLayout,
    children: pricingRoutes,
    errorElement: <DefaultErrorElement />,
  },
  settingsRoute,
  chatRoute,
  {
    path: '/billing/*',
    lazy: () =>
      import('src/features/billing/BillingLayout').then((m) => ({
        Component: m.BillingLayout,
      })),
    children: invoiceRoutes,
    errorElement: <DefaultErrorElement />,
  },
  {
    path: '/reports/*',
    lazy: () =>
      import('src/features/reports/ReportsLayout').then((m) => ({
        Component: m.ReportsLayout,
      })),
    children: reportsRoutes,
    errorElement: <DefaultErrorElement />,
  },
  {
    path: '/admin/*',
    Component: AdminLayout,
    children: adminRoutes,
    errorElement: <DefaultErrorElement />,
  },
  {
    path: '/notifications/*',
    lazy: () =>
      import('src/features/notifications/NotificationsLayout').then((m) => ({
        Component: m.NotificationsLayout,
      })),
    children: notificationsRoutes,
    errorElement: <DefaultErrorElement />,
  },
  {
    path: '*',
    element: <NotFoundRoute />,
    errorElement: <DefaultErrorElement />,
  },
]);

export const rideOrderingPortalRoutes: RouteObject[] = withRootLayout([
  {
    path: '/',
    element: <Navigate to="/trips" />,
    errorElement: <DefaultErrorElement />,
  },
  liveMapRoute,
  teamRoute(rideOrderingPortalTeamRoutes),
  passengerRoute,
  {
    path: '/trips/*',
    element: <DefaultRouteLayout />,
    children: rideOrderingPortalTripRoutes,
    errorElement: <DefaultErrorElement />,
  },
  {
    path: '*',
    element: <NotFoundRoute />,
    errorElement: <DefaultErrorElement />,
  },
]);

export const restrictedSupportRoutes: RouteObject[] = withRootLayout([
  {
    path: '/',
    element: <Navigate to="/trips" />,
    errorElement: <DefaultErrorElement />,
  },
  liveMapRoute,
  teamRoute(teamRoutes),
  fleetRoute,
  passengerRoute,
  {
    path: '/trips/*',
    element: <DefaultRouteLayout />,
    children: tripRoutes,
    errorElement: <DefaultErrorElement />,
  },
  payerRoute,
  settingsRoute,
  chatRoute,
  {
    path: '*',
    element: <NotFoundRoute />,
    errorElement: <DefaultErrorElement />,
  },
]);

const routesToRolesMap: Record<RoleEnum, RouteObject[]> = {
  owner: routes,
  admin: routes,
  dispatcher: routes,
  associate: routes,
  'facility admin': rideOrderingPortalRoutes,
  'facility user': rideOrderingPortalRoutes,
  bambi: routes,
  'restricted support': restrictedSupportRoutes,
  // integration "users" should only interact via the API, not the UI
  integration: [],
};
export function Routing() {
  const { role } = useUserRoles();

  // role should always be defined at this point
  // if it's not, we currently mount all routes
  // What is shown to user is handled in the NavBar component
  // TODO: There's probably a more robust/standard way of handling route access
  // using react-router
  // Will need to circle back after getting restricted support role out
  const routesForUser = role ? routesToRolesMap[role] : routes;

  const router = createBrowserRouter(routesForUser);
  return <RouterProvider router={router} />;
}
